import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Globe2, FileSearch, Film, Clock, Mic2, Info, Radio, Menu, X, ShieldAlert, Megaphone, ScrollText, Syringe } from 'lucide-react';
import DisclosureBanner from './DisclosureBanner';

const NAV = [
  { to: '/', label: 'Accueil', icon: Globe2 },
  { to: '/archives', label: 'OVNIs', icon: FileSearch },
  { to: '/cia', label: 'CIA / FBI', icon: ShieldAlert },
  { to: '/ordres-executifs', label: 'Ordres Exécutifs', icon: ScrollText },
  { to: '/covid', label: 'COVID', icon: Syringe },
  { to: '/videos', label: 'Vidéos', icon: Film },
  { to: '/divulgations', label: 'Divulgations', icon: Megaphone },
  { to: '/chronologie', label: 'Chronologie', icon: Clock },
  { to: '/temoignages', label: 'Témoignages', icon: Mic2 },
  { to: '/a-propos', label: 'À propos', icon: Info },
];

const MARQUEE_ITEMS = [
  'ARCHIVES SOUVERAINES · LA DIVULGATION TOTALE',
  'BREAKING — FAUCI INVOQUE LE 5ÈME AMENDEMENT · SÉNAT 29.07.2026',
  '1 141 PAGES DE JOURNAUX INTIMES · SÉNATEUR RAND PAUL',
  'NON CLASSIFIÉ · POUR DIFFUSION PUBLIQUE',
  '4 ENTITÉS · OVNIS · CIA/FBI · ORDRES EXÉCUTIFS · COVID',
  'BREAKING — FAUCI INVOQUE LE 5ÈME AMENDEMENT · SÉNAT 29.07.2026',
  'RAPPORT FINAL 2026 · CHAMBRE · 500 PAGES · ORIGINE LABORATOIRE',
  'DIARY-GATE · 1 141 PAGES DE JOURNAUX INTIMES · FAUCI',
  'EO 13848 · INGÉRENCE ÉLECTORALE ÉTRANGÈRE',
  'EO 13818 · MAGNITSKY MONDIAL · GEL DES AVOIRS',
  'EO 14395 · TASK FORCE ANTI-FRAUDE · JD VANCE',
  '494 DOCUMENTS DÉCLASSIFIÉS · PURSUE RELEASE 04',
  'CIA · UFOS FACT OR FICTION · STARGATE · GATEWAY PROCESS',
  'ALERTE — BIO-LABS DÉCLASSIFIÉS · 120+ LABORATOIRES · 30 PAYS',
  'MAISON BLANCHE — DIVULGATION UAP CONFIRMÉE',
  'TRADUCTION FRANÇAISE OFFICIEUSE · ACCÈS PUBLIC',
  'RECHERCHE LOCALE — AUCUNE TÉLÉMÉTRIE',
];

export default function Layout({ children }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const utc = time.toLocaleTimeString('fr-FR', { hour12: false, timeZone: 'Europe/Paris' });

  return (
    <div className="relative min-h-screen bg-field text-1">
      {/* Major disclosure alert banner */}
      <DisclosureBanner />

      {/* Top status marquee */}
      <div className="relative overflow-hidden border-b border-white/5 bg-black/40">
        <div className="flex whitespace-nowrap py-1.5 mono text-[10px] tracking-[0.2em] text-3">
          <div className="marquee-track flex shrink-0">
            {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((t, i) => (
              <span key={i} className="mx-6 inline-flex items-center gap-2">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-cyan-400/70" /> {t}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-white/5 bg-[rgba(20,20,20,0.85)] backdrop-blur-md">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-3">
          <Link to="/" className="flex items-center gap-3 group" data-testid="brand-link">
            <div className="relative">
              <div className="h-9 w-9 rounded-sm border border-amber-400/40 flex items-center justify-center bg-amber-500/10 group-hover:bg-amber-500/20 transition-colors">
                <ScrollText className="h-4 w-4 text-amber-300" />
              </div>
              <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-amber-400 pulse-dot" />
            </div>
            <div className="leading-tight">
              <div className="mono text-[10px] tracking-[0.22em] text-3 uppercase">Édition Française · Accès Public</div>
              <div className="serif text-base font-medium tracking-wide">
                ARCHIVES <span className="text-amber-300">SOUVERAINES</span> <span className="text-3 text-sm">· La Divulgation Totale</span>
              </div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map(({ to, label, icon: Icon }) => {
              const active = location.pathname === to || (to !== '/' && location.pathname.startsWith(to));
              return (
                <Link
                  key={to}
                  to={to}
                  data-testid={`nav-${to.replace(/\//g, '') || 'home'}`}
                  className={`relative inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
                    active ? 'text-amber-200 bg-amber-400/10' : 'text-2 hover:text-1 hover:bg-white/5'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{label}</span>
                  {active && <span className="absolute -bottom-px left-3 right-3 h-px bg-amber-300/80" />}
                </Link>
              );
            })}
          </nav>

          <div className="hidden md:flex items-center gap-3 mono text-[11px] text-3">
            <span className="inline-flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-dot" />
              EN LIGNE
            </span>
            <span className="text-4">|</span>
            <span>{utc} <span className="text-4">Paris</span></span>
          </div>

          <button onClick={() => setOpen(o => !o)} className="lg:hidden btn-ghost p-2 rounded-md">
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-white/5 px-6 py-3 bg-[rgba(20,20,20,0.95)]">
            {NAV.map(({ to, label, icon: Icon }) => {
              const active = location.pathname === to || (to !== '/' && location.pathname.startsWith(to));
              return (
                <Link key={to} to={to} className={`flex items-center gap-3 py-2.5 text-sm ${active ? 'text-amber-200' : 'text-2'}`}>
                  <Icon className="h-4 w-4" /> {label}
                </Link>
              );
            })}
          </div>
        )}
      </header>

      <main className="relative">{children}</main>

      <footer className="border-t border-white/5 bg-black/40 mt-24">
        <div className="mx-auto max-w-[1400px] px-6 py-12 grid md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-8 w-8 rounded-sm border border-amber-400/40 flex items-center justify-center bg-amber-500/10">
                <ScrollText className="h-4 w-4 text-amber-300" />
              </div>
              <div className="serif text-lg">ARCHIVES <span className="text-amber-300">SOUVERAINES</span></div>
            </div>
            <p className="text-2 text-sm max-w-md">
              Portail français indépendant des archives d'État déclassifiées : dossiers OVNIs & surnaturel,
              déclassifications CIA/FBI, et ordres exécutifs. Traductions officieuses, sources publiques,
              accès libre — sans mot de passe.
            </p>
            <p className="mt-3 text-sm">
              <span className="text-3">Site créé par </span>
              <span className="text-amber-300 font-medium">Antoine CUTTITTA</span>
              <span className="text-3"> · </span>
              <span className="text-amber-300">Alliance Humaine 2020</span>
            </p>
          </div>
          <div>
            <div className="mono text-[11px] text-3 tracking-widest uppercase mb-3">Navigation</div>
            <ul className="space-y-2 text-sm">
              {NAV.map(n => (
                <li key={n.to}><Link to={n.to} className="text-2 hover:text-cyan-200">{n.label}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="mono text-[11px] text-3 tracking-widest uppercase mb-3">Sources officielles</div>
            <ul className="space-y-2 text-sm">
              <li><a className="text-2 hover:text-cyan-200" href="https://www.war.gov/UFO/" target="_blank" rel="noreferrer">war.gov/UFO</a></li>
              <li><a className="text-2 hover:text-cyan-200" href="https://www.aaro.mil/" target="_blank" rel="noreferrer">aaro.mil</a></li>
              <li><a className="text-2 hover:text-cyan-200" href="https://ufo.gpt2077.com/" target="_blank" rel="noreferrer">ufo.gpt2077.com</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/5 py-4 mono text-[11px] text-3 text-center tracking-wider">
          ARCHIVES SOUVERAINES · SOURCES PUBLIQUES · AUCUNE AFFILIATION GOUVERNEMENTALE
          <div className="mt-1.5 text-[10px] text-4">
            © {new Date().getFullYear()} · Site créé par Antoine CUTTITTA · Alliance Humaine 2020
          </div>
        </div>
      </footer>
    </div>
  );
}
